import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = '/api';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    x: ['M18 6 6 18', 'M6 6l12 12'],
    arrowLeft: ['M19 12H5', 'M12 19l-7-7 7-7'],
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
    truck: ['M1 3h15v13H1z', 'M16 8h4l3 3v5h-7V8z', 'M5.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z', 'M18.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z'],
    chevronDown: 'M6 9l6 6 6-6',
    chevronUp: 'M18 15l-6-6-6 6'
};

export default function TripHistory({ tripId, onBack, user }) {
    const [trip, setTrip] = useState(null);
    const [instances, setInstances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedInstance, setExpandedInstance] = useState(null);

    const operatorId = user?.operatorId || user?._id || 'all';

    useEffect(() => {
        fetchData();
    }, [tripId]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [tripRes, instRes] = await Promise.all([
                axios.get(`${API}/trips/${operatorId}`), // We'll find our trip here
                axios.get(`${API}/trip-instances/${operatorId}`)
            ]);
            
            const foundTrip = tripRes.data.find(t => t._id === tripId);
            setTrip(foundTrip);
            
            const tripInstances = instRes.data
                .filter(inst => (inst.masterTripId?._id || inst.masterTripId) === tripId)
                .sort((a, b) => new Date(b.tripDate) - new Date(a.tripDate));
            
            setInstances(tripInstances);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading history...</div>;
    if (!trip) return <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>Trip not found.</div>;

    return (
        <div style={{ padding: '20px 40px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 30 }}>
                <button 
                    onClick={onBack}
                    style={{ 
                        width: 40, height: 40, borderRadius: 12, border: '1px solid #e2e8f0', 
                        background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', 
                        cursor: 'pointer', color: '#64748b' 
                    }}
                >
                    <Icon d={icons.arrowLeft} size={20} />
                </button>
                <div>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Trip ID: {String(trip._id).slice(-6).toUpperCase()}</div>
                    <h1 style={{ fontSize: 24, fontWeight: 900, color: '#1e293b', margin: 0 }}>Execution History</h1>
                    <p style={{ color: '#64748b', margin: '4px 0 0', fontWeight: 600 }}>{trip.clientId?.companyName} • {trip.routeId?.name} • {trip.tripFrequency}</p>
                </div>
            </div>

            <div style={{ background: 'white', borderRadius: 20, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: '#f8fafc' }}>
                        <tr>
                            <th style={{ textAlign: 'left', padding: '16px 24px', fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Date</th>
                            <th style={{ textAlign: 'left', padding: '16px 24px', fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Vehicle & Driver</th>
                            <th style={{ textAlign: 'left', padding: '16px 24px', fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Status</th>
                            <th style={{ textAlign: 'left', padding: '16px 24px', fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Passengers</th>
                            <th style={{ width: 60 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {instances.length === 0 ? (
                            <tr>
                                <td colSpan="5" style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
                                    No historical executions found for this trip.
                                </td>
                            </tr>
                        ) : (
                            instances.map(inst => (
                                <React.Fragment key={inst._id}>
                                    <tr 
                                        onClick={() => setExpandedInstance(expandedInstance === inst._id ? null : inst._id)}
                                        style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.2s' }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#fcfdfe'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <td style={{ padding: '16px 24px', fontSize: 14, fontWeight: 800, color: '#1e293b' }}>
                                            {new Date(inst.tripDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                        </td>
                                        <td style={{ padding: '16px 24px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                                                    <Icon d={icons.truck} size={14} />
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{inst.vehicleId?.plateNumber || inst.vehicleId?.registrationNumber || '—'}</div>
                                                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{inst.driverId?.name || '—'}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ padding: '16px 24px' }}>
                                            <span className={`md-badge md-badge-${inst.status?.toLowerCase()}`}>{inst.status}</span>
                                            {inst.status === 'Completed' && inst.completedAt && (
                                                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, fontWeight: 700 }}>
                                                    at {new Date(inst.completedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ padding: '16px 24px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#e0e7ff', color: '#4338ca', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>
                                                    {inst.passengers?.length || 0}
                                                </div>
                                                <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Passengers</span>
                                            </div>
                                        </td>
                                        <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                                            <Icon d={expandedInstance === inst._id ? icons.chevronUp : icons.chevronDown} size={16} color="#94a3b8" />
                                        </td>
                                    </tr>
                                    {expandedInstance === inst._id && (
                                        <tr>
                                            <td colSpan="5" style={{ background: '#f8fafc', padding: '0 24px 24px' }}>
                                                <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', marginTop: -8, overflow: 'hidden' }}>
                                                    <div style={{ padding: '12px 16px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase' }}>
                                                        Passenger Manifest for {new Date(inst.tripDate).toLocaleDateString('en-GB')}
                                                    </div>
                                                    <div style={{ padding: 16 }}>
                                                        {(!inst.passengers || inst.passengers.length === 0) ? (
                                                            <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>No passengers recorded for this instance.</div>
                                                        ) : (
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                                                                {inst.passengers.map(p => (
                                                                    <div key={p.passengerId?._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, background: '#f8fafc', borderRadius: 8, border: '1px solid #f1f5f9' }}>
                                                                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: p.passengerId?.gender === 'Female' ? '#fdf2f8' : '#eff6ff', color: p.passengerId?.gender === 'Female' ? '#db2777' : '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                            <Icon d={icons.user} size={14} />
                                                                        </div>
                                                                        <div>
                                                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>{p.passengerId?.name || 'Unknown'}</div>
                                                                            <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{p.passengerId?.employeeId || 'No ID'} • {p.attendanceStatus}</div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
